import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { TeranetGuardModalComponent } from '../teranet-guard-modal/modal.component';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { ICON } from '@core/util/icon';
import {
	PdfModalComponent,
	PdfModalData,
} from '@components/pdf-modal/pdf-modal.component';
import { MatterUploadedFilesSelectors } from '@state/matter/uploaded-files/matter-uploaded-files.selectors';
import { MatterFile } from '@models/matter-file';
import { openCloseWithState } from '@core/animations/animations';
import { TestHooks } from './title-search.hooks';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';

enum SearchType {
	OWNER = 'owner',
	ADDRESS = 'address',
	PIN = 'pin',
	INSTRUMENT = 'instrument',
}

const TitleSearchOptions = [
	{ label: 'Owner', value: 'owner' },
	{ label: 'Address', value: 'address' },
	{ label: 'PIN', value: 'pin' },
	{ label: 'Instrument number', value: 'instrument' },
];

@UntilDestroy()
@Component({
	selector: 'app-teranet-title-search',
	templateUrl: './title-search.component.html',
	styleUrls: ['./title-search.component.scss'],
	animations: [openCloseWithState()],
	standalone: false,
})
export class TitleSearchComponent implements OnInit {
	testHooks = TestHooks;
	public searchType = SearchType;
	public selectedSearchType = SearchType.OWNER;
	public purchaseDeleted = true;
	public loginModalOpen = false;
	public ICON = ICON;
	public showSearch = true;
	public lroSelected = true;

	titleSearchHolders = TitleSearchOptions;
	@Input() showBackToOverview = false;
	@Input() isOverview = true;
	@Output() searchCompletedEvent = new EventEmitter<boolean>();

	@Select(SelectedMatter.loading) matterLoading$: Observable<boolean>;

	@SelectSnapshot(SelectedMatter.id) saleId: string;
	@SelectSnapshot(MatterUploadedFilesSelectors.getLatestAPS)
	lastestAPS: MatterFile;
	@SelectSnapshot(SelectedMatter.requiresTeranetLogin) requiresLogin: boolean;

	drawerService = inject(DrawerService);

	constructor(
		private dialog: MatDialog,
		private routeService: NavigationService,
	) {}

	ngOnInit(): void {
		this.matterLoading$
			.pipe(untilDestroyed(this))
			.subscribe((matterLoading) => {
				if (this.requiresLogin && !matterLoading) {
					if (!this.loginModalOpen) {
						const ref = this.dialog.open(TeranetGuardModalComponent, {
							maxWidth: '550px',
							width: '90%',
						});
						ref
							.afterClosed()
							.pipe(take(1))
							.subscribe(() => {
								this.loginModalOpen = false;
							});
						this.loginModalOpen = true;
					}
				}
			});
	}

	onChangeSearchCriteria(searchTypeValue): void {
		this.selectedSearchType = searchTypeValue;
		this.searchCompletedEvent.emit(false);
	}

	onSearchByOwnerSelected(): void {
		this.selectedSearchType = SearchType.OWNER;
	}

	onSearchByAddressSelected(): void {
		this.selectedSearchType = SearchType.ADDRESS;
	}

	onSearchByPinSelected(): void {
		this.selectedSearchType = SearchType.PIN;
	}

	onSearchByInstrumentSelected(): void {
		this.selectedSearchType = SearchType.INSTRUMENT;
	}

	goToOverview(): void {
		this.routeService.goToMatter_TitleSearch(this.saleId);
	}

	openFile() {
		const data: PdfModalData = {
			googleSrcUrl: this.lastestAPS.url,
			fileName: this.lastestAPS.name,
		};
		this.drawerService.openDrawer(PdfModalComponent, {
			data,
			title: data.fileName,
			noStyle: true,
		});
	}

	toggleSearch() {
		this.showSearch = !this.showSearch;
	}

	onSearchCompleted(event: boolean) {
		this.searchCompletedEvent.emit(event);
		if (event) this.showSearch = false;
	}

	onLroNotSelected(event: boolean) {
		this.lroSelected = !event;
	}
}
